<script setup lang="ts">
import { useDefaultSettingsStore } from "~/stores/defaultSettingsStore"
import ButtonDropdownStore from "./Store/ButtonDropdownStore.vue"
import { SUPPORTED_PAGES_ENUM } from "~/common/breadCrumbs"
import ButtonCombo from "./Store/ButtonCombo.vue"
import { goToRoute } from "~/common/helpers"
import HomeProductCard from "./HomeProductCard.vue"
import HomeProductCardSkeleton from "./HomeProductCardSkeleton.vue"
import type { Product } from "~/models/Product"
import type { Image } from "~/models/Image"
/**
 *
 *  Props and emits section
 *
 *
 */

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */
const { t: $t } = useI18n()
const { settings } = useDefaultSettingsStore()
const route = useRoute()
const isHome = computed(() => route.path === "/")

// Fetch offers products
const {
    data: offersData,
    loading: offersLoading,
    get: fetchOffers,
} = useOFetchCustom("public/offers/discount_products")

const offersProducts = computed(() => {
    if (!offersData.value?.data?.data) return []
    return offersData.value.data?.data as Product[]
})

/**
 *
 *  Life cicle events
 *
 *
 */
// Province selection functionality
const {
    showProvinceModal,
    selectedProvince,
    isProvinceSelected,
    getSavedProvince,
    onProvinceChanged,
    initializeProvince,
} = useProvinceSelection()

const { isPartnerMode } = usePartnerBranding()

// Initialize province on component mount
onMounted(() => {
    initializeProvince()
    fetchOffers({ perPage: 3 })
})

// Watch province changes and refetch offers
watch(selectedProvince, () => {
    fetchOffers({ perPage: 3 })
})

// Computed property for display province name
const displayProvinceName = computed(() => {
    if (!selectedProvince.value) {
        return $t("province_selection.select_province")
    }

    // Handle the "None" option specially
    if (selectedProvince.value === $t("province_selection.none_option")) {
        return $t("province_selection.no_province_selected")
    }

    return selectedProvince.value
})

/**
 *
 *  Functions section
 *
 *
 */

// Function to open province selection modal
const openProvinceModal = () => {
    showProvinceModal.value = true
}

// Function to get product image
const getProductImage = (product: Product): string => {
    if (product.images && product.images.length > 0) {
        const firstImage = product.images[0] as Image
        return firstImage.thumb ?? firstImage.origin ?? ""
    }
    return ""
}

// Function to get product price with old price
const getProductPrice = (product: Product) => {
    const hasDiscount =
        product.sale_price && product.sale_price < product.regular_price
    return {
        price: hasDiscount
            ? product.sale_price_formatted
            : product.regular_price_formatted,
        oldPrice: hasDiscount ? product.regular_price_formatted : null,
    }
}
</script>

<template>
    <div class="tw-w-full tw-bg-primary md:tw-bg-white">
        <div class="tw-flex tw-justify-between tw-gap-2 tw-items-center">
            <div class="tw-flex tw-flex-wrap tw-gap-2 lg:tw-gap-8 tw-w-full">
                <div
                    class="tw-text-customGray-200 hover:tw-text-white tw-flex tw-flex-col md:tw-flex-row tw-gap-0 md:tw-gap-2 md:tw-items-center md:tw-justify-between tw-w-full md:tw-py-4"
                >
                    <div
                        class="max-container tw-flex tw-justify-start tw-w-full tw-items-center"
                    >
                        <div
                            class="tw-hidden md:tw-inline tw-mr-4"
                            @click="goToRoute('/')"
                        >
                            <ApplicationLogo
                                class="!tw-w-[180px] !tw-h-[60px]"
                            />
                        </div>
                        <div
                            class="tw-flex tw-flex-col md:tw-flex-row tw-items-start md:tw-items-center tw-w-full tw-gap-2 md:tw-mt-2 tw-mx-2 md:tw-mx-0"
                        >
                            <HomeSearch
                                class="tw-max-w-[520px] tw-mt-4 tw-mb-4 md:tw-my-0"
                            />
                            <!-- Province Selection Button -->
                            <div
                                class="tw-hidden md:tw-flex tw-flex-row tw-w-full tw-justify-between md:tw-justify-start md:tw-w-min tw-h-min"
                            >
                                <q-btn
                                    class="tw-capitalize custom-province tw-font-titilium tw-rounded-full tw-min-w-max sm:tw-min-w-[140px] tw-text-xs sm:tw-text-base tw-font-bold !tw-border-0 !tw-w-max md:tw-bg-primary hover:md:tw-bg-primary-800 !tw-px-4 sm:!tw-py-2 !tw-py-0 tw-text-primary md:tw-text-white"
                                    flat
                                    @click="openProvinceModal"
                                >
                                    <div
                                        class="tw-flex tw-items-center tw-w-full tw-justify-between tw-text-inherit tw-gap-1"
                                    >
                                        <q-icon
                                            name="location_on"
                                            size="18px"
                                        />
                                        <q-item-label
                                            class="tw-px-auto tw-font-titilium"
                                        >
                                            {{ displayProvinceName }}
                                        </q-item-label>
                                        <q-icon
                                            name="keyboard_arrow_down"
                                            size="18px"
                                        />
                                    </div>
                                </q-btn>
                                <ButtonCombo
                                    class="custom-province tw-inline md:tw-hidden"
                                />
                                <ButtonDropdownStore
                                    :menuItem="{
                                        name: $t('home.store'),
                                        route: '/store',
                                        page: SUPPORTED_PAGES_ENUM.Store,
                                    }"
                                    class="tw-inline md:tw-hidden custom-province"
                                />
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="
                            isHome &&
                            (offersLoading || offersProducts.length > 0)
                        "
                        class="tw-bg-secondary tw-py-2 tw-px-5 md:tw-hidden tw-w-full tw-min-w-0 tw-overflow-x-auto tw-overflow-y-hidden tw-flex tw-gap-2 tw-items-center tw-scrollbar-hide tw-snap-x tw-snap-mandatory max-container"
                    >
                        <template v-if="offersLoading">
                            <div
                                v-for="index in 3"
                                :key="index"
                                class="tw-flex-shrink-0 tw-snap-center"
                            >
                                <HomeProductCardSkeleton />
                            </div>
                        </template>
                        <template v-else>
                            <NuxtLink
                                v-for="product in offersProducts"
                                :key="product.id"
                                :to="`/products/${product.slug}`"
                                class="tw-flex-shrink-0 tw-snap-center tw-no-underline"
                            >
                                <HomeProductCard
                                    :image="getProductImage(product)"
                                    :title="product.name"
                                    :price="getProductPrice(product).price"
                                    :oldPrice="
                                        getProductPrice(product).oldPrice
                                    "
                                />
                            </NuxtLink>
                        </template>
                    </div>
                    <div
                        class="md:tw-flex tw-hidden tw-gap-4 tw-w-max tw-flex-1 tw-justify-evenly tw-items-center tw-px-2 max-container"
                    >
                        <ProfileMenu v-if="!isPartnerMode"></ProfileMenu>
                        <ShopCartButton></ShopCartButton>
                    </div>
                </div>
            </div>
        </div>
    </div>

</template>

<style scoped>
.custom-province {
    transition: all 0.2s ease;
}

.custom-province:hover {
    transform: scale(1.02);
}
</style>

<style>
.tw-scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.tw-scrollbar-hide::-webkit-scrollbar {
    display: none;
}
</style>
